import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import moment from 'moment';
import { capitalize, get } from 'lodash';
import Toggle from 'react-toggle';
import 'react-toggle/style.css';
import { toast } from 'react-toastify';
import {
  Alert,
  Badge,
  Card,
  CardHeader,
  CardBody,
  Col,
  Container,
  ListGroup,
  ListGroupItem,
  Row,
  Table,
  Nav,
  NavItem,
  NavLink,
  Modal,
  ModalBody,
  ModalHeader,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
  UncontrolledDropdown,
  UncontrolledTooltip,
} from 'reactstrap';
import { Button } from 'components/Button';
import Loader from 'components/Loader';
import {
  selectLoanId as selectLoanIdAction,
  fetchPaymentMethods as fetchPaymentMethodsAction,
  fetchLoanDetail as fetchLoanDetailAction,
  fetchTransactionsList as fetchTransactionsListAction,
  fetchPaymentsList as fetchPaymentsListAction,
  fetchDocuments as fetchDocumentsAction,
  fetchLoanTerm as fetchLoanTermAction,
  removePaymentMethod as removePaymentMethodAction,
  putPaymentAccount as putPaymentAccountAction,
  postPaymentAccount as postPaymentAccountAction,
  postDebitCardAccount as postDebitCardAccountAction,
  makePayment as makePaymentAction,
  fetchCurrentPayoffInfo as fetchCurrentPayoffInfoAction,
  fetchFuturePayoffInfo as fetchFuturePayoffInfoAction,
  fetchScheduledPayment as fetchScheduledPaymentAction,
  cancelAutopay as cancelAutopayAction,
  putScheduleAutopay as putScheduleAutopayAction,
  getBillingSetting as getBillingSettingAction,
  putBillingSetting as putBillingSettingAction,
  fetchAutopayInfo as fetchAutopayInfoAction,
  fetchAutopays,
  fetchLoanCharges,
  fetchCollateral as fetchCollateralAction,
  fetchCollateralLiquidations as fetchCollateralLiquidationsAction,
  validateBankRoutingNumber as validateBankRoutingNumberAction,
} from 'actions/dashboard';

import PaymentModal from 'components/PaymentModal';
import AddPaymentModal from 'components/AddPaymentMethods';
import TermsModal from 'components/TermsModal';
import LoansAgreementModal from 'components/LoansAgreementModal';
import RecurringPaymentModal from 'components/RecurringPaymentModal';
import DisclosureModal from 'components/DisclosureModal';

import Edit from 'components/Icons/Edit';
import Trash from 'components/Icons/Trash';
import { formatCurrency, formatCurrencyWithSign } from 'utils/formatCurrency';
import { formatPercentage } from 'utils/formatPercentage';
import { downloadCSV } from 'utils/csvExport';
import { normalizePaymentAccounts, derivePrimaryPaymentMethod } from 'utils/paymentAccountMapper';
import { useConfig } from 'config/ConfigContext';
import CancelPaymentModal from 'components/CancelPaymentModal';
import Download from 'components/Icons/Download';
import AutopayUnavailableTooltip from 'components/AutopayUnavailableTooltip';
import CollateralHistoryPanel from '../Dashboard/components/CollateralHistoryPanel';
import LiquidationHistoryPanel from '../Dashboard/components/LiquidationHistoryPanel';
import {
  getCollateralHistoryTransactions,
  mapCollateralHistoryRows,
  mapLiquidationHistoryRows,
  isDtcCryptoLoan,
  isInGracePeriod,
  resolveCollateralDisplayValue,
  resolveTransactionDescription,
  resolvePaymentDescription,
  resolveIsCryptoLoan,
  toLtvPercentage,
} from '../Dashboard/utils/cryptoUtils';
import { AUTOPAY_UNAVAILABLE_MESSAGE, isOnlineAutopayUnavailable } from '../Dashboard/utils/autopayAvailability';
import './styles.scss';

const TAB_COLLATERAL_HISTORY = 3;
const TAB_LIQUIDATIONS = 4;

const ConsumerMyLoans = (props) => {
  const { delay } = useConfig();
  const location = useLocation();
  const [modal, setModal] = useState(false);
  const [isAddOrEditPaymentModalVisible, setIsAddOrEditPaymentModalVisible] = useState(false);
  const [isTermsModalVisible, setIsTermsModalVisible] = useState(false);
  const [isLoanAgreementModal, setIsLoanAgreementModal] = useState(false);
  const [isRecurringPaymentModal, setIsRecurringPaymentModal] = useState(false);
  const [activeTab, setActiveTab] = useState(get(location, 'state.activeTab', 0));
  const [activePaymentTab, setActivePaymentTab] = useState('one-time');
  const [paymentModalType, setPaymentModalType] = useState('add');
  const [currentPaymentMethod, setCurrentPaymentMethod] = useState(null);
  const [isDeleteConfirmationModalVisible, setIsDeleteConfirmationModalVisible] = useState(false);
  const [deleteConfirmationItem, setDeleteConfirmationItem] = useState(null);
  const [isRemovePaymentMethodModalVisible, setIsRemovePaymentMethodModalVisible] = useState(false);
  const [removePaymentMethodItem, setRemovePaymentMethodItem] = useState(null);
  const [isRemovingPaymentMethod, setIsRemovingPaymentMethod] = useState(false);
  const [isDisclosureModalVisible, setIsDisclosureModalVisible] = useState(false);
  const [showPaperlessAlert, setShowPaperlessAlert] = useState(false);
  const [scheduledPaymentDate, setScheduledPaymentDate] = useState('');
  const [isCancelPaymentModal, setIsCancelPaymentModal] = useState(false);
  const [nextPaymentAmt, setNextPaymentAmt] = useState(0);
  const [nextPayment, setNextPayment] = useState(0);
  const [autopayBannerVisible, setAutopayBannerVisible] = useState(false);
  const lateFee = get(props.loanTerm, 'data.lateFeeAmount', '-');

  useEffect(() => {
    const paymentList = get(props, 'paymentsList.data') || [];
    const scheduledPayments = get(props, 'scheduledPaymentsList.data') || [];
    const activePaymentList = [...scheduledPayments, ...paymentList];

    let nextPay = {};
    nextPay = activePaymentList && activePaymentList.length > 0 && activePaymentList?.filter((item) => item && new Date(item?.applyDate || item?.date) > new Date())
      .sort((a, b) => (new Date(a.applyDate || a?.date) < new Date(b.applyDate || a?.date) ? -1 : 1))[0];

    setNextPayment(nextPay);
  }, [props.scheduledPaymentsList, props.paymentsList]);

  useEffect(() => {
    if (nextPayment) {
      setNextPaymentAmt(nextPayment.amount || 0);
    }
  }, [nextPayment]);

  const fetchTabsData = (tab, loanId = props.loansList.activeLoanId) => {
    const {
      fetchTransactionsList,
      fetchPaymentsList,
      fetchScheduledPayment,
    } = props;

    switch (tab) {
      case 1:
        fetchPaymentsList(loanId);
        break;
      case 2:
        fetchScheduledPayment(loanId);
        break;
      case TAB_COLLATERAL_HISTORY:
      case TAB_LIQUIDATIONS:
        break;
      default:
        fetchTransactionsList(loanId);
    }
  };

  useEffect(() => {
    const {
      loansList,
      fetchLoanTerm,
      fetchLoanDetail,
      fetchPaymentMethods,
      getBillingSetting,
      fetchAutopayInfo,
    } = props;

    fetchLoanTerm(loansList.activeLoanId);
    fetchLoanDetail({
      loanId: loansList.activeLoanId,
    });
    fetchPaymentMethods(loansList.activeLoanId);
    getBillingSetting(loansList.activeLoanId);
    fetchAutopayInfo(loansList.activeLoanId);
    props.fetchLoanCharges(loansList.activeLoanId);

    fetchTabsData(activeTab);
    if (resolveIsCryptoLoan(loansList)) {
      props.fetchCollateral(loansList.activeLoanId);
      props.fetchCollateralLiquidations(loansList.activeLoanId);
    }
  }, []);

  const getPaymentMethod = (item) => {
    const accountType = get(item, 'primaryPaymentMethod.type', 'paymentAccount.type.checking');
    if ((accountType === 'paymentAccount.type.checking' || accountType === 'paymentAccount.type.savings') && item.primaryPaymentMethod.checkingAccount.accountNumber) {
      const type = get(item, 'primaryPaymentMethod.checkingAccount.accountType');
      return (
        <>
          <strong>{capitalize(type.split('.')[2])}</strong>
          {` xxxxxx${get(item, 'primaryPaymentMethod.checkingAccount.accountNumber', '').slice(-4)}`}
        </>
      );
    }

    if ((accountType === 'paymentAccount.type.card' || accountType === 'paymentAccount.type.credit') && item.primaryPaymentMethod.creditCard.cardType && item.primaryPaymentMethod.creditCard.cardNumber) {
      return (
        <>
          <strong>{`${capitalize(get(item, 'primaryPaymentMethod.creditCard.cardData.type', ''))}`}</strong>
          {` xxxx-xxxx-xxxx-${get(item, 'primaryPaymentMethod.creditCard.cardNumber', '').slice(-4)}`}
        </>
      );
    }
  };

  const handleActiveTab = (tab) => {
    fetchTabsData(tab);
    setActiveTab(tab);
  };

  const handleCheck = (checked) => {
    const { putBillingSetting, loansList, billingSetting } = props;

    if (billingSetting && !checked) {
      setShowPaperlessAlert(true);
      return;
    }

    putBillingSetting({
      loanId: loansList.activeLoanId,
      data: { eBilling: checked },
    });
  };

  const handleModal = (newActivePaymentTab) => {
    const { loansList } = props;

    if (newActivePaymentTab === 'autopay' && isOnlineAutopayUnavailable(props.loanTerm, loansList)) {
      return;
    }

    if (newActivePaymentTab === 'manageAutoPay') {
      setActiveTab(2);
      fetchTabsData(2);
    } else if (!props.paymentMethods.isLoading && !modal) {
      props.fetchLoanCharges(loansList.activeLoanId);
      props.fetchPaymentMethods({
        success: () => {
          setModal(!modal);
          setActivePaymentTab(newActivePaymentTab);
        },
        fail: (err) => {
          if (err.status === 404) {
            setModal(!modal);
            setActivePaymentTab(newActivePaymentTab);
          }
        },
      });
    } else {
      setModal(!modal);
      setActivePaymentTab(newActivePaymentTab);
    }
  };

  const handleTermsModal = () => {
    setIsTermsModalVisible(!isTermsModalVisible);
  };

  const handleLoanAgreementModal = () => {
    setIsLoanAgreementModal(!isLoanAgreementModal);
  };

  const handleRecurringModal = () => {
    setIsRecurringPaymentModal(!isRecurringPaymentModal);
  };

  const handleCancelPaymentModal = () => {
    setIsCancelPaymentModal(!isCancelPaymentModal);
  };

  const handleAddPaymentModal = () => {
    setIsAddOrEditPaymentModalVisible(!isAddOrEditPaymentModalVisible);
    setPaymentModalType('add');
    setCurrentPaymentMethod(null);
  };

  const handleEditPaymentModal = (item) => {
    if (!isAddOrEditPaymentModalVisible) {
      setCurrentPaymentMethod(item);
    }
    setIsAddOrEditPaymentModalVisible(!isAddOrEditPaymentModalVisible);
    setPaymentModalType('save');
  };

  const handleChange = (e) => {
    const { loansList } = props;
    const nextLoan = loansList.data?.find((loan) => loan.loanId === e.target.value);
    const nextIsCrypto = isDtcCryptoLoan(nextLoan);

    if (!nextIsCrypto && (activeTab === TAB_COLLATERAL_HISTORY || activeTab === TAB_LIQUIDATIONS)) {
      setActiveTab(0);
    }

    props.getBillingSetting(e.target.value);
    props.selectLoanId(e.target.value);
    props.fetchLoanDetail({ loanId: e.target.value });
    props.fetchPaymentsList(e.target.value);
    props.fetchPaymentMethods();
    props.fetchTransactionsList(e.target.value);
    props.fetchDocuments(e.target.value);
    props.fetchLoanTerm(e.target.value);
    props.fetchScheduledPayment(e.target.value);
    props.fetchAutopayInfo(e.target.value);
    props.fetchLoanCharges(e.target.value);
    if (nextIsCrypto) {
      props.fetchCollateral(e.target.value);
      props.fetchCollateralLiquidations(e.target.value);
    }
    setAutopayBannerVisible(false);
  };

  const toggleRemovingPaymentMethod = (item) => {
    setIsRemovePaymentMethodModalVisible(!isRemovePaymentMethodModalVisible);
    setRemovePaymentMethodItem(item);
    setIsRemovingPaymentMethod(false);
  };

  const savePaymentAccount = (data, cb) => {
    if (data) {
      if (paymentModalType === 'add') {
        props.postPaymentAccount({
          data,
          success: (res) => {
            if (cb) { cb(true); }
            toast.success(
              (
                <div>
                  <h5>{get(res, 'data.message', 'Payment method added successfully!')}</h5>
                </div>
              ),
              { autoClose: true }
            );
            props.fetchPaymentMethods();
            setIsAddOrEditPaymentModalVisible(false);
          },
          fail: (err) => {
            if (cb) { cb(); }
            if (err.status === 400) {
              toast.error(
                (
                  <div>
                    <h5>There was an error saving your payment account</h5>
                    <small>{get(err, 'data.message', 'Error occured')}</small>
                  </div>
                ),
                { autoClose: true }
              );
            }
          },
        });
      } else {
        props.putPaymentAccount({
          data,
          paymentAccountId: data.paymentAccountId,
          success: (res) => {
            if (cb) { cb(true); }
            toast.success(
              (
                <div>
                  <h5>{get(res, 'data.message', 'Payment method edited successfully!')}</h5>
                </div>
              ),
              { autoClose: true }
            );
            props.fetchPaymentMethods();
            setIsAddOrEditPaymentModalVisible(false);
          },
          fail: (err) => {
            if (cb) { cb(); }
            if (err.status === 400) {
              toast.error(
                (
                  <div>
                    <h5>There was an error editing your payment account</h5>
                    <small>{get(err, 'data.message', 'Error occured')}</small>
                  </div>
                ),
                { autoClose: true }
              );
            }
          },
        });
      }
    }
  };

  const saveDebitCardToken = (data, cb) => {
    if (data) {
      if (paymentModalType === 'add') {
        props.postDebitCardAccount({
          data,
          success: (res) => {
            if (cb) { cb(true); }
            toast.success(
              (
                <div>
                  <h5>{get(res, 'data.message', 'Payment method added successfully!')}</h5>
                </div>
              ),
              { autoClose: true }
            );
            props.fetchPaymentMethods();
            setIsAddOrEditPaymentModalVisible(false);
          },
          fail: (err) => {
            if (cb) { cb(); }
            if (err.status === 400 || err.status === 500) {
              toast.error(
                (
                  <div>
                    <h5>There was an error saving your payment account</h5>
                    <small>{get(err, 'data.message', 'Error occured')}</small>
                  </div>
                ),
                { autoClose: true }
              );
            }
          },
        });
      } else {
        props.putPaymentAccount({
          data,
          paymentAccountId: data.paymentAccountId,
          success: (res) => {
            if (cb) { cb(true); }
            toast.success(
              (
                <div>
                  <h5>{get(res, 'data.message', 'Payment method edited successfully!')}</h5>
                </div>
              ),
              { autoClose: true }
            );
            props.fetchPaymentMethods();
            setIsAddOrEditPaymentModalVisible(false);
          },
          fail: (err) => {
            if (cb) { cb(); }
            if (err.status === 400 || err.status === 500) {
              toast.error(
                (
                  <div>
                    <h5>There was an error editing your payment account</h5>
                    <small>{get(err, 'data.message', 'Error occured')}</small>
                  </div>
                ),
                { autoClose: true }
              );
            }
          },
        });
      }
    }
  };

  const toggleDisclosureModal = (date = null) => {
    setIsDisclosureModalVisible(!isDisclosureModalVisible);
    if (date) {
      setScheduledPaymentDate(date);
    } else {
      setScheduledPaymentDate('');
    }
  };

  const putMakePayment = (data, cb) => {
    const {
      loansList,
      makePayment,
      fetchScheduledPayment,
      fetchTransactionsList,
      fetchPaymentsList,
      fetchLoanDetail,
    } = props;
    props.fetchAutopays({
      loanId: loansList.activeLoanId,
      success: (res1) => {
        makePayment({
          data,
          loanId: loansList.activeLoanId,
          success: (res) => {
            if (res && res.some((item) => get(item, 'paymentProcessor.status') === 'Failed')) {
              toast.error(
                (
                  <div>
                    <h5>We encountered an error while processing payment</h5>
                  </div>
                ),
                { autoClose: true }
              );
              return;
            }

            toast.success(
              (
                <div>
                  <h5>{get(res, 'data.message', 'Payment made successfully!')}</h5>
                </div>
              ),
              { autoClose: true }
            );
            const autopayArr = res1.slice();

            if (autopayArr.length) {
              autopayArr.sort((a, b) => new Date(a.processDateTime) - new Date(b.processDateTime));
              const firstAutopayAfterToday = autopayArr.find((item) => (new Date(item.processDateTime) - new Date()) > 0 && `${get(item, 'recurringFrequency.id')}`.toLowerCase() !== 'autopay.recurringfrequency.single');
              if (firstAutopayAfterToday) {
                toggleDisclosureModal(get(firstAutopayAfterToday, 'processDateTime'));
              }
            }

            fetchScheduledPayment(loansList.activeLoanId);
            fetchTransactionsList(loansList.activeLoanId);
            fetchPaymentsList(loansList.activeLoanId);
            fetchLoanDetail({
              loanId: loansList.activeLoanId,
              delay,
            });
            props.fetchLoanCharges(loansList.activeLoanId);
            setModal(false);
            cb();
          },
          fail: (err) => {
            if (err.status === 400) {
              toast.error(
                (
                  <div>
                    <h5>There was an error processing your payment</h5>
                    <small>{get(err, 'data.message', 'Error occured!')}</small>
                  </div>
                ),
                { autoClose: true }
              );
            }
            cb();
          },
        });
      },
    });
  };

  const putScheduleAutopay = (data, cb) => {
    const { loansList } = props;
    props.fetchAutopays({
      loanId: loansList.activeLoanId,
      success: (res1) => {
        props.putScheduleAutopay({
          data,
          loanId: loansList.activeLoanId,
          success: (res) => {
            toast.success(
              (
                <div>
                  <h5>{get(res, 'data.message', data.type === 'autopay.type.single' ? 'Payment scheduled successfully.' : 'AutoPay was scheduled successfully!')}</h5>
                </div>
              ),
              { autoClose: true }
            );

            const autopayArr = res1.slice();

            if (autopayArr.length && data.type === 'autopay.type.single') {
              autopayArr.sort((a, b) => new Date(a.processDateTime) - new Date(b.processDateTime));
              const firstAutopayAfterToday = autopayArr.find((item) => (new Date(item.processDateTime) - new Date()) > 0 && `${get(item, 'recurringFrequency.id')}`.toLowerCase() !== 'autopay.recurringfrequency.single');
              if (firstAutopayAfterToday) {
                toggleDisclosureModal(get(firstAutopayAfterToday, 'processDateTime'));
              }
            }

            setModal(false);
            props.fetchScheduledPayment(loansList.activeLoanId);
            props.fetchTransactionsList(loansList.activeLoanId);
            props.fetchPaymentsList(loansList.activeLoanId);
            props.fetchLoanDetail({
              loanId: loansList.activeLoanId,
              delay,
            });
            props.fetchAutopayInfo(loansList.activeLoanId);
            props.fetchLoanCharges(loansList.activeLoanId);
            cb();
          },
          fail: (err) => {
            toast.error(
              (
                <div>
                  <h5>There was an error scheduling autopay</h5>
                  <small>{get(err, 'data.message', 'Error occured!')}</small>
                </div>
              ),
              { autoClose: true }
            );
            cb();
          },
        });
      },
      fail: (err) => {
        toast.error(
          (
            <div>
              <h5>There was an error scheduling autopay</h5>
              <small>{get(err, 'data.message', 'Error occured!')}</small>
            </div>
          ),
          { autoClose: true }
        );
        cb();
      },
    });
  };

  const loanInfoBody = () => {
    const { loanTerm, loanDetail, loansList } = props;
    const isCryptoLoan = resolveIsCryptoLoan(loansList);

    return (
      loanTerm.data && loanDetail.data
        ? (
          <Table className="mb-0" size="sm" responsive striped borderless>
            <tbody>
              <tr>
                <th scope="row">Principal Amount</th>
                <td className="text-right">
                  $
                  {formatCurrency(get(loanTerm, 'data.loanAmount', 0) + get(loanTerm, 'data.underwriting', 0), 2)}
                </td>
              </tr>
              <tr>
                <th scope="row">Contract Date</th>
                <td className="text-right">
                  {get(loanTerm, 'data.contractDate', '') ? moment(new Date(get(loanTerm, 'data.contractDate', ''))).format('MM/DD/YYYY').toString() : '-'}
                </td>
              </tr>
              <tr>
                <th scope="row">Loan Term</th>
                <td className="text-right">
                  {get(loanTerm, 'data.loanTerm', 0)}
                  {' '}
                  Months
                </td>
              </tr>
              <tr>
                <th scope="row">Interest Rate</th>
                <td className="text-right">{formatPercentage(get(loanTerm, 'data.loanRate', 0))}</td>
              </tr>
              <tr>
                <th scope="row">Monthly Payment Amount</th>
                <td className="text-right">
                  $
                  {formatCurrency(get(loanTerm, 'data.regularPayment', 0), 2)}
                </td>
              </tr>
              <tr>
                <th scope="row">Final Payment Amount</th>
                <td className="text-right">
                  $
                  {formatCurrency(get(loanDetail, 'data.0.finalPaymentAmount', 0), 2)}
                </td>
              </tr>
              <tr>
                <th scope="row">Final Payment Date</th>
                <td className="text-right">{moment(new Date(get(loanDetail, 'data.0.finalPaymentDate', ''))).format('MM/DD/YYYY').toString()}</td>
              </tr>
              {isCryptoLoan && (
                <tr>
                  <th scope="row">LTV</th>
                  <td className="text-right">
                    {`${toLtvPercentage(get(loanTerm, 'data.loanToValue', '-'))}%`}
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )
        : <Loader className="height-2" />
    );
  };

  const handleDelete = (item) => {
    setIsDeleteConfirmationModalVisible(true);
    setDeleteConfirmationItem(item);
  };

  const handleCancelAutopay = (id) => {
    const {
      loansList,
      cancelAutopay,
      fetchScheduledPayment,
    } = props;

    cancelAutopay({
      data: {
        loanId: loansList.activeLoanId,
        id,
      },
      success: () => {
        fetchScheduledPayment(loansList.activeLoanId);
      },
    });
  };

  const handleScheduledModal = () => {
    setIsDeleteConfirmationModalVisible(!isDeleteConfirmationModalVisible);
  };

  const handleCSVExport = () => {
    const { transactionsList, paymentsList } = props;
    let dataToExport = [];
    let fileName = '';

    if (activeTab === 0) {
      dataToExport = (transactionsList?.data || []).map((item) => {
        const isPayment = item.type.id === 'payment' || item.type.id === 'credit' || item.type.id === 'advancement';
        return ({
          date: moment(new Date(item.date)).format('MM/DD/YYYY').toString(),
          description: resolveTransactionDescription(item),
          principal: formatCurrencyWithSign(isPayment ? item.paymentPrincipal : item.chargePrincipal, 2),
          interest: formatCurrencyWithSign(isPayment ? item.paymentInterest : item.chargeInterest, 2),
          fees: formatCurrencyWithSign(isPayment ? item.paymentFees : item.chargeFees, 2),
          total: formatCurrencyWithSign(isPayment ? item.paymentAmount : item.chargeAmount, 2),
        });
      });
      fileName = 'transaction-history';
    } else if (activeTab === 1) {
      dataToExport = (paymentsList?.data || []).map((item) => ({
        date: moment(new Date(item.date)).format('MM/DD/YYYY').toString(),
        description: resolvePaymentDescription(item),
        status: item.status.label,
        amount: `$${formatCurrency(item.amount, 2)}`,
        reversalReason: item.reverseReason,
      }));
      fileName = 'payment-history';
    } else if (activeTab === TAB_COLLATERAL_HISTORY) {
      const { loanDetail: exportLoanDetail, collateral: exportCollateral } = props;
      const exportCoinType = get(exportLoanDetail, 'data.0.coinType');
      const exportTransactions = getCollateralHistoryTransactions(exportCollateral?.data);
      const exportRows = mapCollateralHistoryRows(exportTransactions, exportCoinType);
      dataToExport = exportRows.map((item) => ({
        date: moment(new Date(item.date)).format('MM/DD/YYYY').toString(),
        type: item.typeLabel,
        amount: item.amountDisplay,
        status: item.statusLabel,
        transactionId: item.transactionHash,
      }));
      fileName = 'collateral-history';
    }

    if (dataToExport.length > 0) {
      downloadCSV(dataToExport, fileName);
    }
  };

  const renderTabContent = (tabIndex) => {
    const { transactionsList, paymentsList, scheduledPaymentsList, loanDetail } = props;
    const transactions = get(transactionsList, 'data');
    const payments = get(paymentsList, 'data');
    const scheduledPayments = get(scheduledPaymentsList, 'data');

    switch (tabIndex) {
      case TAB_COLLATERAL_HISTORY: {
        const collateralCoinType = get(loanDetail, 'data.0.coinType');
        return (
          <CollateralHistoryPanel
            loanId={props.loansList.activeLoanId}
            coinType={collateralCoinType}
          />
        );
      }
      case TAB_LIQUIDATIONS:
        return (
          <LiquidationHistoryPanel
            loanId={props.loansList.activeLoanId}
            coinType={get(loanDetail, 'data.0.coinType')}
          />
        );
      case 1:
        return (
          payments === undefined
            ? <Loader className="height-2" />
            : (
              <Table className="mb-0" size="sm" responsive striped borderless>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Status</th>
                    <th className="text-right">Amount</th>
                    <th className="reversal-reason-column">Reversal Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {
                  payments.length > 0 ? payments.map((item, index) => (
                    <tr key={index} className={item.reverseReason ? 'reversal-reason-row' : ''}>
                      <th scope="row">{moment(new Date(item.date)).format('MM/DD/YYYY').toString()}</th>
                      <td>{resolvePaymentDescription(item)}</td>
                      <td>{resolveCollateralDisplayValue(item.status)}</td>
                      <td className="text-right">
                        $
                        {formatCurrency(item.amount, 2)}
                      </td>
                      <td className="reversal-reason-column">
                        {item.reverseReason || '-'}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={4} align="center" className="pt-3 pb-3">No data</td>
                    </tr>
                  )
                }
                </tbody>
              </Table>
            )
        );
      case 2:
        return (
          scheduledPayments === undefined
            ? <Loader className="height-2" />
            : (
              <Table className="mb-0" size="sm" responsive striped borderless>
                <thead>
                  <tr>
                    <th>Next Payment Date</th>
                    <th>Payments Remaining</th>
                    <th>Frequency</th>
                    <th className="text-right">Amount</th>
                    <th>Payment Method</th>
                    <th aria-label="empty header" />
                  </tr>
                </thead>
                <tbody>
                  {
                  scheduledPayments.length > 0 ? scheduledPayments.map((item, index) => (
                    <tr key={index}>
                      <th scope="row">{moment(new Date(item.processDateTime)).format('MM/DD/YYYY').toString()}</th>
                      <td>{item.recurringPeriods}</td>
                      <td>{resolveCollateralDisplayValue(item.recurringFrequency)}</td>
                      <td className="text-right">
                        $
                        {formatCurrency(item.amount, 2)}
                      </td>
                      <td>
                        {getPaymentMethod(item)}
                      </td>
                      <td>
                        {item.recurringFrequency.label === 'Single' && (
                          <div style={{ cursor: 'pointer' }} onClick={handleDelete.bind(null, item)}>
                            <Trash />
                          </div>
                        )}
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td colSpan={6} align="center" className="pt-3 pb-3">No data</td>
                    </tr>
                  )
                }
                </tbody>
              </Table>
            )
        );
      default:
        return (
          transactions === undefined
            ? <Loader className="height-2" />
            : (
              <Table className="mb-0" size="sm" responsive striped borderless>
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th className="text-right">Principal</th>
                    <th className="text-right">Interest</th>
                    <th className="text-right">Fees</th>
                    <th className="text-right">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {
                  transactions.length > 0 ? transactions.map((item, index) => {
                    if (item.type.id === 'payment' || item.type.id === 'credit' || item.type.id === 'advancement') {
                      return (
                        <tr key={index}>
                          <th scope="row">{moment(new Date(item.date)).format('MM/DD/YYYY').toString()}</th>
                          <td>{resolveCollateralDisplayValue(item.type)}</td>
                          <td className="text-right">
                            {formatCurrencyWithSign(item.paymentPrincipal, 2)}
                          </td>
                          <td className="text-right">
                            {formatCurrencyWithSign(item.paymentInterest, 2)}
                          </td>
                          <td className="text-right">
                            {formatCurrencyWithSign(item.paymentFees, 2)}
                          </td>
                          <td className="text-right">
                            <strong>
                              {formatCurrencyWithSign(item.paymentAmount, 2)}
                            </strong>
                          </td>
                        </tr>
                      );
                    }
                    return (
                      <tr key={index}>
                        <th scope="row">{moment(new Date(item.date)).format('MM/DD/YYYY').toString()}</th>
                        <td>{resolveTransactionDescription(item)}</td>
                        <td className="text-right">
                          {formatCurrencyWithSign(item.chargePrincipal, 2)}
                        </td>
                        <td className="text-right">
                          {formatCurrencyWithSign(item.chargeInterest, 2)}
                        </td>
                        <td className="text-right">
                          {formatCurrencyWithSign(item.chargeFees, 2)}
                        </td>
                        <td className="text-right">
                          <strong>
                            {formatCurrencyWithSign(item.chargeAmount, 2)}
                          </strong>
                        </td>
                      </tr>
                    );
                  }) : (
                    <tr>
                      <td colSpan={6} align="center" className="pt-3 pb-3">No data</td>
                    </tr>
                  )
                }
                </tbody>
              </Table>
            )
        );
    }
  };

  const renderDeleteConfirmationModal = () => (deleteConfirmationItem && deleteConfirmationItem.recurringFrequency.id === 'autopay.recurringFrequency.monthly' ? (
    <Modal isOpen={isDeleteConfirmationModalVisible} toggle={handleScheduledModal} size="md">
      <ModalHeader toggle={handleScheduledModal}>Turn Off Autopay</ModalHeader>
      <ModalBody className="text-center">
        <p>If you turn off AutoPay, all future payments will be canceled.</p>
        <h4 className="mb-3">Are you sure you want to turn off AutoPay?</h4>
        <Button
          onClick={() => {
            handleCancelAutopay(deleteConfirmationItem.id);
            handleScheduledModal();
          }}
          color="danger"
          className="w-100 mb-1"
        >
          Turn Off Autopay
        </Button>
        <Button onClick={handleScheduledModal} className="w-100">Go back</Button>
      </ModalBody>
    </Modal>
  ) : (
    <Modal isOpen={isDeleteConfirmationModalVisible} toggle={handleScheduledModal} size="md">
      <ModalHeader toggle={handleScheduledModal}>Cancel scheduled payment</ModalHeader>
      <ModalBody className="text-center">
        <h4 className="mb-3">Are you sure you want to cancel this payment?</h4>
        <Button
          onClick={() => {
            handleCancelAutopay(deleteConfirmationItem.id);
            handleScheduledModal();
          }}
          color="danger"
          className="w-100 mb-1"
        >
          CANCEL THIS SCHEDULED PAYMENT
        </Button>
        <Button onClick={handleScheduledModal} className="w-100">Go back</Button>
      </ModalBody>
    </Modal>
  ));

  const renderPaymentType = (item) => (typeof item.type === 'string' ? item.type : get(item, 'type.2') || '');

  const {
    loanDetail,
    billingSetting,
    autopay,
    paymentMethods,
    loansList,
    removePaymentMethod,
    loanCharges,
    channel,
    maxLateFee,
  } = props;

  const pastDueRaw = Number(get(loanDetail, 'data.0.daysPastDue', 0));
  const pastDue = Number.isNaN(pastDueRaw) ? 0 : pastDueRaw;
  const showGracePeriod = isInGracePeriod(pastDue);
  const nextPaymentAmount = get(loanDetail, 'data.0.nextPaymentAmount') && formatCurrency(get(loanDetail, 'data.0.nextPaymentAmount'), 2).split('.');
  const pastDueAmount = formatCurrency(get(loanDetail, 'data.0.amountDue'), 2).split('.');
  const dueFees = Number(get(loanCharges, 'data.unpaidCharges'));
  const totalDueAmount = get(loanDetail, 'data.0.totalDue');
  const totalDueDisplay = formatCurrency(Number(totalDueAmount || 0), 2).split('.');
  const nextPaymentDate = get(loanDetail, 'data.0.nextPaymentDate', '') ? moment(new Date(get(loanDetail, 'data.0.nextPaymentDate', ''))).format('MM/DD/YYYY').toString() : '-';
  const gracePeriodDeadline = pastDue > 0 ? moment().subtract(pastDue, 'days').add(16, 'days').format('MM/DD/YYYY') : nextPaymentDate;
  const activeloanId = loansList.activeLoanId;
  const allpaymentmethods = paymentMethods.data
    ? normalizePaymentAccounts(paymentMethods.data)
    : [];

  const primaryPaymentMethod = paymentMethods.data
    ? derivePrimaryPaymentMethod(paymentMethods.data)
    : '';

  const isAutoPayEnabled = get(autopay, 'data.isAutoPayEnabled', false);
  const isCrypto = resolveIsCryptoLoan(loansList);
  const isAutopayUnavailable = isOnlineAutopayUnavailable(props.loanTerm, loansList);
  const hasLiquidations = isCrypto && (
    Boolean(props.collateralLiquidations?.isLoading)
    || mapLiquidationHistoryRows(props.collateralLiquidations?.data).length > 0
  );
  const showCollateralHistoryDownload = isCrypto
    && activeTab === TAB_COLLATERAL_HISTORY
    && !loanDetail?.isLoading;

  return (
    <div className="page-myloans pb-3">
      <Container fluid>
        {autopayBannerVisible && (
          <Alert color="warning" className="mb-3 position-relative">
            <div className="d-flex align-items-start">
              <span>
                We&apos;re unable to disable Autopay for your account online. Please give us a call at (888) 502-8082, or send us your written request by email at CustomerCare@LendingUSA.com with the details of your request, and we&apos;ll be happy to assist you.
              </span>
              <button
                type="button"
                className="close ml-2"
                aria-label="Close"
                onClick={() => setAutopayBannerVisible(false)}
              >
                <span aria-hidden="true">&times;</span>
              </button>
            </div>
          </Alert>
        )}
        {showPaperlessAlert && (
          <Alert color="warning" className="d-flex align-items-start">
            <span className="flex-grow-1">
              We&apos;re unable to disable paperless billing for your account online.
              Please give us a call at (888) 502-8082, or send us your written request
              by email at CustomerCare@LendingUSA.com with the details of your request,
              and we&apos;ll be happy to assist you. Please note that withdrawing this
              E-Consent will not affect the legal effectiveness, validity, or
              enforceability of any electronic disclosures provided prior to the withdrawal.
            </span>
            <button
              type="button"
              className="close ml-2"
              aria-label="Close"
              onClick={() => setShowPaperlessAlert(false)}
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </Alert>
        )}
        <Row className="mb-3 align-items-center">
          <Col className="text-center text-md-left">
            <h3 className="mb-0">Loan Details</h3>
          </Col>
          <Col xs={12} md="auto" className="text-right justify-content-end ml-md-3 mt-3 mt-md-0">
            <div className="mb-0 input-inline pb-0 form-group">
              <UncontrolledDropdown inNavbar>
                <DropdownToggle
                  nav
                  caret
                  style={{
                    padding: '.675rem 1.125rem',
                    height: 'auto',
                    backgroundColor: 'white',
                    border: '2px solid #dbe3eb',
                    color: '#495057',
                    fontSize: '1rem',
                    marginBottom: '0px !important',
                  }}
                  className="custom-dropdown-toggle form-control"
                >
                  {activeloanId && loansList.data
                    ? loansList.data.find((item) => item.loanId === activeloanId)?.externalId || 'Select'
                    : 'Select'}
                </DropdownToggle>
                <DropdownMenu>
                  <DropdownItem end>Select</DropdownItem>
                  {loansList.data && loansList.data.map((item, index) => (
                    <DropdownItem
                      key={`loanlist_${index}`}
                      onClick={() => handleChange({ target: { value: item.loanId } })}
                      active={activeloanId === item.loanId}
                    >
                      {item.externalId}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </UncontrolledDropdown>
            </div>
          </Col>
        </Row>

        <Row>
          <Col md={6} lg={4}>

            <Card inverse color="primary">
              <CardBody className="text-center">
                <Row>
                  <Col className="pr-0">
                    <Card className="pt-2 pb-2" style={{ height: '110px' }}>
                      {
                        loanDetail.data ? (
                          <>
                            <h6>Next Payment</h6>
                            {nextPaymentAmount ? (
                              <h3>
                                $
                                {nextPaymentAmount[0]}
                                .
                                <small>{nextPaymentAmount[1]}</small>
                              </h3>
                            ) : <h3>-</h3>}
                            <h6 className="mb-0">
                              <span>
                                Due by
                                {' '}
                                {loanDetail.data ? nextPaymentDate : '-'}
                              </span>
                            </h6>
                          </>
                        ) : (
                          <Loader className="light height-100" />
                        )
                      }
                    </Card>
                  </Col>
                  <Col>
                    <Card className={`pt-2 pb-2 ${pastDue > 0 ? 'bg-white' : ''}`} style={{ height: '110px' }}>
                      {
                        loanDetail.data ? (
                          <>
                            <h6 className={pastDue > 0 ? 'text-danger' : ''}>Total Due</h6>
                            <h3 className={`${pastDue > 0 ? 'text-danger' : ''}`}>
                              $
                              {totalDueDisplay[0]}
                              .
                              <small className={`${pastDue > 0 ? 'text-danger' : ''}`}>{totalDueDisplay[1]}</small>
                            </h3>
                            <h6 className={`mb-0 ${pastDue > 0 ? 'text-danger' : ''}`}>
                              {pastDue > 0 ? 'DUE NOW' : 'UP TO DATE'}
                            </h6>
                          </>
                        ) : (
                          <Loader className="light height-100" />
                        )
                      }
                    </Card>
                  </Col>
                </Row>
                {showGracePeriod && (
                  <div
                    style={{
                      padding: '14px 16px',
                      borderRadius: 14,
                      border: '2px solid rgba(255,255,255,0.35)',
                      background: 'rgba(255,255,255,0.14)',
                      color: '#ffffff',
                      fontWeight: 600,
                      lineHeight: 1.35,
                      marginTop: 14,
                      marginBottom: 14,
                    }}
                  >
                    {`A $${formatCurrency(Number(lateFee || 0), 2)} late fee may be applied if the past due amount is not paid by ${gracePeriodDeadline}.${isAutoPayEnabled ? ' Contact us to reschedule autopay to avoid this fee.' : ''}`}
                  </div>
                )}
                {!isAutoPayEnabled && (
                  isAutopayUnavailable ? (
                    <AutopayUnavailableTooltip message={AUTOPAY_UNAVAILABLE_MESSAGE}>
                      <Button color="light" outline className="w-100" isDisabled style={{ pointerEvents: 'none' }}>
                        <img src="/icons/autopay.svg" alt="" className="mr-1" />
                        Set Up Autopay
                      </Button>
                    </AutopayUnavailableTooltip>
                  ) : (
                    <Button color="light" outline className="w-100 mb-1" onClick={handleModal.bind(null, 'autopay')} isLoading={autopay.isLoading}>
                      <img src="/icons/autopay.svg" alt="" className="mr-1" />
                      Set Up Autopay
                    </Button>
                  )
                )}
                <Button color="light" className="w-100" onClick={handleModal.bind(null, 'one-time')}>
                  <img src="/icons/payment.svg" alt="" className="mr-1" />
                  Make A Payment
                </Button>
                <Row className="mt-3">
                  <Col className="text-left">
                    <strong>Paperless Billing</strong>
                  </Col>
                  <Col className="text-right">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <small className="mr-1"><strong>{billingSetting ? 'ON' : 'OFF'}</strong></small>
                      <Toggle
                        checked={billingSetting}
                        onChange={(e) => {
                          const value = e.target.checked;
                          handleCheck(value);
                        }}
                      />
                    </div>
                  </Col>
                </Row>
                <hr className="my-2" />
                <Row>
                  <Col className="text-left">
                    <strong>Autopay</strong>
                  </Col>
                  <Col className="text-right">
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center' }}>
                      <small className="mr-1"><strong>{isAutoPayEnabled ? 'ON' : 'OFF'}</strong></small>
                      {isAutoPayEnabled ? (
                        <Toggle
                          checked
                          onChange={() => setAutopayBannerVisible(true)}
                        />
                      ) : autopay.isLoading ? (
                        <Toggle
                          checked={false}
                          onChange={() => {}}
                        />
                      ) : isAutopayUnavailable ? (
                        <AutopayUnavailableTooltip message={AUTOPAY_UNAVAILABLE_MESSAGE} width={280} inline>
                          <span>
                            <Toggle
                              checked={false}
                              disabled
                              onChange={() => {}}
                            />
                          </span>
                        </AutopayUnavailableTooltip>
                      ) : (
                        <span id="autopay-toggle-off">
                          <Toggle
                            checked={false}
                            disabled
                            onChange={() => {}}
                          />
                          <UncontrolledTooltip placement="top" target="autopay-toggle-off">
                            Turn on Autopay by clicking the Set Up Autopay button above.
                          </UncontrolledTooltip>
                        </span>
                      )}
                    </div>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>Payment Methods</CardHeader>
              {
                paymentMethods.isLoading
                  ? (
                    <ListGroup>
                      <ListGroupItem>
                        <Loader className="height-2" />
                      </ListGroupItem>
                    </ListGroup>
                  )
                  : (
                    <ListGroup style={{ margin: 0 }}>
                      { paymentMethods && paymentMethods.data && paymentMethods.data.length > 0
                        ? allpaymentmethods.map((item, index) => (
                          <ListGroupItem key={index} className="position-relative">
                            <div style={{ paddingRight: 70, position: 'relative' }}>
                              <p className="mb-0">
                                <strong>{capitalize(renderPaymentType(item))}</strong>
                                {['debit', 'credit'].indexOf(renderPaymentType(item).toLowerCase()) !== -1 ? ' xxxx-xxxx-xxxx-' : ' xxxxxx'}
                                {item.accountNumber.slice(-4)}
                              </p>
                              <small className="text-capitalize">
                                {item.brand || item.accountName}
                                {' '}
&nbsp;
                                {item.isPrimary && <Badge color="primary">PRIMARY</Badge>}
                              </small>
                              <Button color="link" className="btn-icon position-absolute" style={{ top: 0, right: 35 }} onClick={handleEditPaymentModal.bind(null, item)}>
                                <Edit />
                              </Button>
                              <Button color="link" className="btn-icon position-absolute" style={{ top: 0, right: 0 }} onClick={toggleRemovingPaymentMethod.bind(null, item)}>
                                <Trash />
                              </Button>
                            </div>
                          </ListGroupItem>
                        )) : (
                          <div className="noPaymentMethodsData">
                            <p className="mb-0">No payment method yet.</p>
                          </div>
                        )}
                      <ListGroupItem tag="a" onClick={handleAddPaymentModal} className="d-flex">
                        <img src="/icons/add-primary.svg" alt="Add" className="mr-1" />
                        <strong>Add Payment Method</strong>
                      </ListGroupItem>
                    </ListGroup>
                  )
              }
            </Card>

            <Card>
              <CardHeader>{isCrypto ? 'Original Loan Terms' : 'Loan Info'}</CardHeader>
              {loanInfoBody()}
            </Card>

          </Col>
          <Col md={6} lg={8}>
            <Card>
              <Nav className="LoansNav" tabs>
                {(
                  activeTab === 0
                  || activeTab === 1
                  || showCollateralHistoryDownload
                ) && !(
                  props.transactionsList?.isLoading
                  || props.paymentsList?.isLoading
                  || (showCollateralHistoryDownload && (props.collateral?.isLoading || props.collateralSummary?.isLoading))
                ) ? (
                  <div className="downloadBtn" onClick={handleCSVExport}>
                    <Download />
                    <div className="tooltipText">Download as CSV</div>
                  </div>
                  ) : null}
                <NavItem>
                  <NavLink className={`${activeTab === 0 && 'active'}`} onClick={handleActiveTab.bind(null, 0)}>
                    Transactions
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={`${activeTab === 1 && 'active'}`} onClick={handleActiveTab.bind(null, 1)}>
                    Payments
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink className={`${activeTab === 2 && 'active'}`} onClick={handleActiveTab.bind(null, 2)}>
                    Scheduled
                    <span> Payments</span>
                  </NavLink>
                </NavItem>
                {isCrypto ? (
                  <NavItem>
                    <NavLink
                      className={`${activeTab === TAB_COLLATERAL_HISTORY && 'active'}`}
                      onClick={handleActiveTab.bind(null, TAB_COLLATERAL_HISTORY)}
                    >
                      Collateral
                    </NavLink>
                  </NavItem>
                ) : null}
                {hasLiquidations ? (
                  <NavItem>
                    <NavLink
                      className={`${activeTab === TAB_LIQUIDATIONS && 'active'}`}
                      onClick={handleActiveTab.bind(null, TAB_LIQUIDATIONS)}
                    >
                      Liquidations
                    </NavLink>
                  </NavItem>
                ) : null}
              </Nav>
              {renderTabContent(activeTab)}
            </Card>

          </Col>
        </Row>

      </Container>

      <PaymentModal
        isOpen={modal}
        toggle={handleModal}
        nextPaymentAmount={nextPaymentAmount}
        nextPaymentDate={nextPaymentDate}
        pastDueAmount={pastDueAmount}
        dueFees={dueFees}
        totalDueAmount={totalDueAmount}
        paymentMethods={allpaymentmethods}
        putMakePayment={putMakePayment}
        putScheduleAutopay={putScheduleAutopay}
        toggleTermsModal={handleTermsModal}
        toggleLoanAgreementModal={handleLoanAgreementModal}
        toggleRecurringModal={handleRecurringModal}
        activeTab={activePaymentTab}
        primaryPaymentMethod={primaryPaymentMethod}
        fetchCurrentPayoffInfo={props.fetchCurrentPayoffInfo}
        fetchFuturePayoffInfo={props.fetchFuturePayoffInfo}
        addPaymentMethod={handleAddPaymentModal}
        toggleCancelPaymentModal={handleCancelPaymentModal}
        channel={channel}
        nextPaymentAmt={nextPaymentAmt}
        nextPaymentApplyDate={nextPayment?.applyDate ?? nextPayment?.date ?? ''}
      />

      <AddPaymentModal
        isOpen={isAddOrEditPaymentModalVisible}
        toggle={paymentModalType === 'add' ? handleAddPaymentModal : handleEditPaymentModal}
        saveToken={savePaymentAccount}
        saveDebitCardToken={saveDebitCardToken}
        paymentModalType={paymentModalType}
        currentPaymentMethod={currentPaymentMethod}
        validateBankRoutingNumber={props.validateBankRoutingNumber}
        onUseDifferentBankAccount={() => {
          setCurrentPaymentMethod(null);
          setPaymentModalType('add');
        }}
      />

      <DisclosureModal
        isOpen={isDisclosureModalVisible}
        toggle={toggleDisclosureModal}
        scheduledPaymentDate={scheduledPaymentDate}
      />

      <TermsModal
        isOpen={isTermsModalVisible}
        toggle={handleTermsModal}
        channel={channel}
        fee={maxLateFee}
      />

      <LoansAgreementModal
        isOpen={isLoanAgreementModal}
        toggle={handleLoanAgreementModal}
      />

      <RecurringPaymentModal
        isOpen={isRecurringPaymentModal}
        toggle={handleRecurringModal}
      />

      <CancelPaymentModal
        isOpen={isCancelPaymentModal}
        toggle={handleCancelPaymentModal}
      />

      {renderDeleteConfirmationModal()}

      <Modal isOpen={isRemovePaymentMethodModalVisible} toggle={toggleRemovingPaymentMethod} size="md">
        <ModalHeader toggle={toggleRemovingPaymentMethod}>Delete Payment Method</ModalHeader>
        <ModalBody className="text-center">
          <h4 className="mb-3">Are you sure you want to remove this payment method?</h4>
          <Button
            onClick={() => {
              if (isRemovingPaymentMethod) return;
              setIsRemovingPaymentMethod(true);
              removePaymentMethod({
                paymentAccountId: removePaymentMethodItem.paymentAccountId,
                success: () => {
                  toast.success(
                    (
                      <div>
                        <h5>Payment method removed successfully.</h5>
                      </div>
                    ),
                    { autoClose: true }
                  );
                  setIsRemovingPaymentMethod(false);
                  toggleRemovingPaymentMethod();
                  props.fetchPaymentMethods();
                },
                fail: (err) => {
                  toast.error(
                    (
                      <div>
                        <h5>{get(err, 'data.message', 'Failed to remove payment method')}</h5>
                      </div>
                    ),
                    { autoClose: true }
                  );
                  setIsRemovingPaymentMethod(false);
                },
              });
            }}
            color="danger"
            className="w-100 mb-1"
            isLoading={isRemovingPaymentMethod}
            isDisabled={isRemovingPaymentMethod}
          >
            Delete Payment Method
          </Button>
          <Button onClick={toggleRemovingPaymentMethod} className="w-100">Go back</Button>
        </ModalBody>
      </Modal>
    </div>
  );
};

ConsumerMyLoans.propTypes = {
  fetchCurrentPayoffInfo: PropTypes.func.isRequired,
  fetchFuturePayoffInfo: PropTypes.func.isRequired,
  fetchPaymentsList: PropTypes.func.isRequired,
  fetchAutopayInfo: PropTypes.func.isRequired,
  fetchLoanTerm: PropTypes.func.isRequired,
  fetchLoanDetail: PropTypes.func.isRequired,
  selectLoanId: PropTypes.func.isRequired,
  fetchPaymentMethods: PropTypes.func.isRequired,
  fetchTransactionsList: PropTypes.func.isRequired,
  fetchScheduledPayment: PropTypes.func.isRequired,
  putScheduleAutopay: PropTypes.func.isRequired,
  makePayment: PropTypes.func.isRequired,
  putPaymentAccount: PropTypes.func.isRequired,
  postPaymentAccount: PropTypes.func.isRequired,
  postDebitCardAccount: PropTypes.func.isRequired,
  removePaymentMethod: PropTypes.func.isRequired,
  fetchDocuments: PropTypes.func.isRequired,
  cancelAutopay: PropTypes.func.isRequired,
  getBillingSetting: PropTypes.func.isRequired,
  putBillingSetting: PropTypes.func.isRequired,
  fetchAutopays: PropTypes.func.isRequired,
  fetchLoanCharges: PropTypes.func.isRequired,
  fetchCollateral: PropTypes.func.isRequired,
  fetchCollateralLiquidations: PropTypes.func.isRequired,
  scheduledPaymentsList: PropTypes.object.isRequired,
  loansList: PropTypes.object.isRequired,
  loanDetail: PropTypes.object.isRequired,
  loanTerm: PropTypes.object.isRequired,
  paymentsList: PropTypes.object.isRequired,
  transactionsList: PropTypes.object.isRequired,
  paymentMethods: PropTypes.object.isRequired,
  billingSetting: PropTypes.bool.isRequired,
  autopay: PropTypes.object.isRequired,
  loanCharges: PropTypes.object.isRequired,
  collateral: PropTypes.object.isRequired,
  collateralSummary: PropTypes.object.isRequired,
  collateralLiquidations: PropTypes.object.isRequired,
  channel: PropTypes.string.isRequired,
  maxLateFee: PropTypes.string.isRequired,
  validateBankRoutingNumber: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  loansList: state.dashboard.loansList,
  loanDetail: state.dashboard.loanDetail,
  loanTerm: state.dashboard.loanTerm,
  transactionsList: state.dashboard.transactionsList,
  paymentsList: state.dashboard.paymentsList,
  paymentMethods: state.dashboard.paymentMethods,
  putPaymentMethod: state.dashboard.putPaymentMethod,
  makePaymentMessage: state.dashboard.makePaymentMessage,
  remove: state.dashboard.remove,
  scheduledPaymentsList: state.dashboard.scheduledPaymentsList,
  billingSetting: state.dashboard.billingSetting.data ? state.dashboard.billingSetting.data.eBilling : false,
  channel: state.dashboard.billingSetting.data && state.dashboard.billingSetting.data.customFields ? state.dashboard.billingSetting.data.customFields.channel : '',
  autopay: state.dashboard.autopay,
  loanCharges: state.dashboard.loanCharges,
  collateral: state.dashboard.collateral,
  collateralSummary: state.dashboard.collateralSummary,
  collateralLiquidations: state.dashboard.collateralLiquidations,
  maxLateFee: state.dashboard.billingSetting && state.dashboard.billingSetting.data && state.dashboard.billingSetting.data.customFields ? state.dashboard.billingSetting.data.customFields.maxLateFee : null,
});

const mapDispatchToProps = {
  selectLoanId: selectLoanIdAction,
  fetchPaymentMethods: fetchPaymentMethodsAction,
  fetchLoanDetail: fetchLoanDetailAction,
  fetchTransactionsList: fetchTransactionsListAction,
  fetchPaymentsList: fetchPaymentsListAction,
  fetchDocuments: fetchDocumentsAction,
  fetchLoanTerm: fetchLoanTermAction,
  removePaymentMethod: removePaymentMethodAction,
  putPaymentAccount: putPaymentAccountAction,
  postPaymentAccount: postPaymentAccountAction,
  postDebitCardAccount: postDebitCardAccountAction,
  makePayment: makePaymentAction,
  fetchCurrentPayoffInfo: fetchCurrentPayoffInfoAction,
  fetchFuturePayoffInfo: fetchFuturePayoffInfoAction,
  fetchScheduledPayment: fetchScheduledPaymentAction,
  cancelAutopay: cancelAutopayAction,
  putScheduleAutopay: putScheduleAutopayAction,
  getBillingSetting: getBillingSettingAction,
  putBillingSetting: putBillingSettingAction,
  fetchAutopayInfo: fetchAutopayInfoAction,
  fetchAutopays,
  fetchLoanCharges,
  fetchCollateral: fetchCollateralAction,
  fetchCollateralLiquidations: fetchCollateralLiquidationsAction,
  validateBankRoutingNumber: validateBankRoutingNumberAction,
};

export default compose(
  connect(mapStateToProps, mapDispatchToProps)
)(ConsumerMyLoans);
